import React from 'react';
import { aboutCards, features, plans, posts, faqs } from './marketingContent';

type MarketingPageProps = {
  onPrimaryCta: () => void;
  onSecondaryCta: () => void;
};

export function MarketingPage({ onPrimaryCta, onSecondaryCta }: MarketingPageProps) {
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="landing">
      <div className="landing-inner">
        <header className="landing-header">
          <div className="container landing-nav">
            <div className="nav-brand">
              <div className="brand-icon brand-icon-frame">
                <img src="/logo-transparent.png" alt="" />
              </div>
              <div>
                <div className="nav-title">EDGAR Insights</div>
                <strong>Product tour</strong>
              </div>
            </div>
            <nav aria-label="Product">
              <ul className="nav-links">
                <li><a href="/">Home</a></li>
                <li><a href="#about">About</a></li>
                <li><a href="#features">Features</a></li>
                <li><a href="#pricing">Pricing</a></li>
                <li><a href="#blog">Blog</a></li>
                <li><a href="#faq">FAQ</a></li>
              </ul>
            </nav>
            <div className="nav-actions">
              <button className="cta-secondary" type="button" onClick={onSecondaryCta}>
                Sign in
              </button>
              <button className="cta-button" type="button" onClick={onPrimaryCta}>
                Get started
              </button>
            </div>
          </div>
        </header>

        <main>
          <section className="hero" id="top">
            <div className="container hero-grid">
              <div className="hero-copy">
                <span className="hero-eyebrow">Product overview</span>
                <h1 className="hero-title">
                  Everything about EDGAR Insights in one pass.
                </h1>
                <p className="hero-lede">
                  Explore the why, the how, the plans, and the FAQs without bouncing between sections on the home page.
                </p>
                <div className="hero-actions">
                  <button className="cta-button" type="button" onClick={onPrimaryCta}>
                    Start free
                  </button>
                  <button className="cta-secondary" type="button" onClick={() => scrollTo('features')}>
                    Jump to features
                  </button>
                </div>
              </div>
              <div className="hero-panel" aria-label="Product quick facts">
                <h3>Quick stats</h3>
                <div className="panel-metric">
                  <span>Crawl cadence</span>
                  <strong>Every 10 minutes</strong>
                </div>
                <div className="panel-metric">
                  <span>Coverage</span>
                  <strong>92% of EDGAR feed</strong>
                </div>
                <div className="panel-metric">
                  <span>Delivery</span>
                  <strong>REST, webhooks, Slack/Email</strong>
                </div>
              </div>
            </div>
          </section>

          <section id="about" className="section">
            <div className="container">
              <div className="section-heading">
                <div>
                  <div className="section-eyebrow">About</div>
                  <h2 className="section-title">We turn SEC filings into pre-market clarity.</h2>
                </div>
                <p className="section-description">
                  EDGAR Insights is a focused layer for desks that trade on fundamentals and events. We pair relentless
                  ingestion with models trained on SEC language so you see what changed and why it matters, fast.
                </p>
              </div>
              <div className="about-grid">
                {aboutCards.map((card) => (
                  <div className="about-card" key={card.title}>
                    <h3>{card.title}</h3>
                    <p>{card.copy}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="features" className="section">
            <div className="container">
              <div className="section-heading">
                <div>
                  <div className="section-eyebrow">Features</div>
                  <h2 className="section-title">Built for analysts, quants, and risk in one pane.</h2>
                </div>
                <p className="section-description">
                  A designed surface for the filing tape: fast scans, auditable insights, and delivery options that meet
                  your workflow instead of adding one more dashboard tab.
                </p>
              </div>
              <div className="feature-grid">
                {features.map((feature) => (
                  <div className="feature-card" key={feature.title}>
                    <h3>{feature.title}</h3>
                    <p>{feature.blurb}</p>
                    <ul>
                      {feature.bullets.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="pricing" className="section">
            <div className="container">
              <div className="section-heading">
                <div>
                  <div className="section-eyebrow">Pricing</div>
                  <h2 className="section-title">Start free. Scale when the signals prove out.</h2>
                </div>
                <p className="section-description">
                  No procurement maze. Pick a plan, ship your first alerts, and upgrade when you need unlimited coverage
                  and delivery channels.
                </p>
              </div>
              <div className="pricing-grid">
                {plans.map((plan) => (
                  <div className={`pricing-card ${plan.recommended ? 'recommended' : ''}`} key={plan.name}>
                    <h3>{plan.name}</h3>
                    <div className="price">
                      {plan.price} <small>{plan.cadence}</small>
                    </div>
                    <p className="price-note">{plan.note}</p>
                    <ul className="plan-features">
                      {plan.features.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                    <button
                      className={plan.recommended ? 'cta-button' : 'cta-secondary'}
                      type="button"
                      onClick={plan.recommended ? onPrimaryCta : onSecondaryCta}
                    >
                      {plan.recommended ? 'Upgrade to Pro' : 'Start free'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="blog" className="section">
            <div className="container">
              <div className="section-heading">
                <div>
                  <div className="section-eyebrow">Blog</div>
                  <h2 className="section-title">Playbooks and engineering notes from the feed.</h2>
                </div>
                <p className="section-description">
                  We document what we learn shipping filing-aware AI: production checklists, prompt guards, and desk
                  playbooks you can reuse.
                </p>
              </div>
              <div className="blog-grid">
                {posts.map((post) => (
                  <div className="blog-card" key={post.title}>
                    <div className="meta">
                      <span>{post.tag}</span>
                      <span>|</span>
                      <span>{post.time}</span>
                    </div>
                    <h3>{post.title}</h3>
                    <a href="#top">Read snapshot</a>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="faq" className="section">
            <div className="container">
              <div className="section-heading">
                <div>
                  <div className="section-eyebrow">FAQ</div>
                  <h2 className="section-title">Answers for desks, compliance, and ops.</h2>
                </div>
                <p className="section-description">
                  If you need something custom - private webhooks, IP allowlists, or SOC2 paperwork - let us know when
                  you onboard and we will set it up.
                </p>
              </div>
              <div className="faq-grid">
                {faqs.map((faq) => (
                  <div className="faq-item" key={faq.q}>
                    <h4>{faq.q}</h4>
                    <p>{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <footer className="landing-footer">
          <div className="container">
            <div className="site-tagline">
              EDGAR Insights | AI-powered trading signals from SEC filings | Built for fundamental and event-driven teams.
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
